import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateItem } from 'Vendor/Store/studio';
import { AppDispatch, RootState } from 'Vendor/Store/store';
import { GridItemStyle } from 'Vendor/Utils/StudioTypes';
import { selectSingleSelectedItem } from 'Vendor/Store/studio/selectors';
import { ActiveLayoutItem } from 'Vendor/Store/studio/initialState';
import { Button, ButtonGroup } from 'react-bootstrap';

const EditImage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const selectedItem = useSelector(selectSingleSelectedItem)
    const [localItem, setLocalItem] = useState<ActiveLayoutItem | null>(null);
    const activePageId = useSelector((state: RootState) => state.studio.present.activePageId);
    const [alignment, setAlignment] = useState({ horizontal: 'center', vertical: 'center' });

    useEffect(() => {
        setLocalItem(selectedItem ? { ...selectedItem } : null);
    }, [selectedItem]);

    const handleUpdate = (updatedItem: ActiveLayoutItem) => {
        if (!activePageId) {
            return;
        }

        setLocalItem(updatedItem);
        dispatch(updateItem({ pageId: activePageId, updatedItem }));
    };

    const handleContentChange = (field: string, value: string) => {
        if (!localItem) return;
        const updatedItem = { ...localItem, content: { ...localItem.content, [field]: value } };
        handleUpdate(updatedItem);
    };

    const handleStyleChange = (style: keyof GridItemStyle, value: string | number | undefined) => {
        if (!localItem) return;
        const updatedItem = { ...localItem, styles: { ...localItem.styles, [style]: value } };
        handleUpdate(updatedItem);
    };

    const handleStyleChangeMultiple = (styles: Partial<GridItemStyle>) => {
        if (!localItem) return;

        const updatedStyles = { ...localItem.styles, ...styles };
        const updatedItem = { ...localItem, styles: updatedStyles };

        handleUpdate(updatedItem);
    };

    const handleAlignmentChange = (axis: 'horizontal' | 'vertical', value: string) => {
        const newAlignment = { ...alignment, [axis]: value };
        setAlignment(newAlignment);

        handleStyleChangeMultiple({
            objectPosition: `${newAlignment.horizontal} ${newAlignment.vertical}`,
            transformOrigin: `${newAlignment.horizontal} ${newAlignment.vertical}`,
        });
    };

    const handleZoom = (direction: 'in' | 'out') => {
        if (!localItem) return;

        const transformStr = localItem.styles.transform || 'scale(1)';
        const match = transformStr.match(/scale\(([\d.]+)\)/);
        const currentZoom = match ? parseFloat(match[1]) : 1;

        let newZoom = direction === 'in' ? currentZoom + 0.1 : currentZoom - 0.1;

        // Clamp between 1 and 5
        newZoom = Math.max(1, Math.min(newZoom, 5));

        // Round to 2 decimals to prevent floating-point drift
        newZoom = Math.round(newZoom * 100) / 100;

        handleStyleChangeMultiple({
            transform: `scale(${newZoom})`,
            transformOrigin: localItem.styles.objectPosition ?? 'center center',
        });
    };

    if (!selectedItem || !localItem || (selectedItem.type !== 'PRODUCT' && selectedItem.type !== 'FILE' && selectedItem.type !== 'EXTERNAL_IMAGE')) {
        return null; // Don't render if the selected item is not an image
    }

    const isVideo = localItem.content.mimeType?.startsWith('video/') || localItem.content.src?.match(/\.(mp4|webm|ogg|mov|m4v|avi|flv)(.*)?$/i);

    return (
        <>
            <div className="my-3">
                <h3 className="h5 fw-medium font-headline mb-1 text-white">Properties</h3>
                <p className="small text-muted text-white mb-0">Editing: {isVideo ? 'video' : localItem.type.toLowerCase()} item</p>
            </div>

            <div className="mb-3">
                <label htmlFor="image-src" className="form-label fs-5 text-white">{isVideo ? 'Video URL' : 'Image URL'}</label>
                <input
                    id="image-src"
                    type="url"
                    className="form-control"
                    value={localItem.content.src || ''}
                    onChange={(e) => handleContentChange('src', e.target.value)}
                />
            </div>

            <h5 className="h5 fw-medium mb-3 text-white">Styling</h5>

            {/* Aligner l'image / la vidéo */}
            <div className="mb-3">
                <label
                    htmlFor="aligner-image"
                    className="form-label fs-5 text-white"
                >
                    {isVideo ? 'Aligner la vidéo' : "Aligner l'image"}
                </label>
                <div className="d-flex gap-2">
                    {/* Horizontal alignment */}
                    <ButtonGroup aria-label="Image alignment" className='w-100'>
                        <Button variant="secondary" onClick={() => handleAlignmentChange('horizontal', 'left')}>
                            <i className="fa-solid fa-arrow-left fs-2"></i>
                        </Button>
                        <Button variant="secondary" onClick={() => handleAlignmentChange('horizontal', 'center')}>
                            <i className="fa-solid fa-ellipsis fs-2"></i>
                        </Button>
                        <Button variant="secondary" onClick={() => handleAlignmentChange('horizontal', 'right')}>
                            <i className="fa-solid fa-arrow-right fs-2"></i>
                        </Button>
                    </ButtonGroup>

                    {/* Vertical alignment */}
                    <ButtonGroup aria-label="Image vertical alignment" className='w-100'>
                        <Button variant="secondary" onClick={() => handleAlignmentChange('vertical', 'top')}>
                            <i className="fa-solid fa-arrow-up fs-2"></i>
                        </Button>
                        <Button variant="secondary" onClick={() => handleAlignmentChange('vertical', 'center')}>
                            <i className="fa-solid fa-ellipsis-vertical fs-2"></i>
                        </Button>
                        <Button variant="secondary" onClick={() => handleAlignmentChange('vertical', 'bottom')}>
                            <i className="fa-solid fa-arrow-down fs-2"></i>
                        </Button>
                    </ButtonGroup>
                    <Button variant="secondary" onClick={() => {
                        handleAlignmentChange('horizontal', 'center')
                        handleAlignmentChange('vertical', 'center')
                    }}>
                        <i className="fa-solid fa-arrows-up-down-left-right fs-2"></i>
                    </Button>
                </div>
            </div>

            {/* Adjuster l'image / la vidéo */}
            <div className="mb-3">
                <label
                    htmlFor="adjuster-image"
                    className="form-label fs-5 text-white"
                >
                    {isVideo ? 'Ajuster la vidéo' : "Adjuster l'image"}
                </label>
                <div className="d-flex gap-2">
                    <ButtonGroup aria-label="Image fit" className='w-100 border'>
                        <Button variant="secondary" onClick={() => handleZoom('out')}>
                            <i className="fa-solid fa-magnifying-glass-minus fs-2"></i>
                        </Button>
                        <Button variant="secondary" onClick={() => handleStyleChange('objectFit', 'contain')}>
                            Adjuster
                        </Button>
                        <Button variant="secondary" onClick={() => handleStyleChange('objectFit', 'cover')}>
                            Remplir
                        </Button>
                        <Button variant="secondary" onClick={() => handleZoom('in')}>
                            <i className="fa-solid fa-magnifying-glass-plus fs-2"></i>
                        </Button>
                    </ButtonGroup>
                </div>
            </div>
            <div className="mb-3">
                <label htmlFor="opacity" className="form-label fs-5 text-white">Opacity</label>
                <input
                    type="range"
                    className="form-range custom-range"
                    id="opacity"
                    min="0"
                    max="1"
                    step="0.1"
                    value={Number(localItem.styles.opacity || 1)}
                    onChange={(e) => handleStyleChange('opacity', e.target.value)}
                />
            </div >
        </>
    )
}

export default EditImage;
